import { createAsyncThunk } from "@reduxjs/toolkit";

import { CSRF_HEADER, ajax, getCSRFToken } from "../../../utils/ajax";
import * as signals from "../../signals";
import { IBasket, ICustomerSummary } from "../models";

interface IStopAssistingCustomerResult {
    customer: ICustomerSummary;
    basket: IBasket;
}

/**
 * Stops assisting the current customer as a CSR agent.
 *
 * This ends the CSR assistance session for the specified customer. After stopping,
 * the CSR will no longer be associated with this customer's session. It fetches
 * the updated state to confirm there is no longer an assisted customer.
 *
 * Side effects:
 * - Emits `onBeforeStopAssistingCustomer` signal before stopping
 * - Emits `onAfterStopAssistingCustomer` signal after completion
 * - These signals can be used by other components to react to assistance state changes
 *
 * @param userURL - The API URL of the customer to stop assisting (e.g., "/api/csr/users/123/")
 * @returns Promise resolving to updated customer and basket state (typically both null)
 *
 * @example
 * dispatch(stopAssistingCustomer(customer.url))
 */
export const stopAssistingCustomer = createAsyncThunk(
    "csr/stopAssistingCustomer",
    async (userURL: string): Promise<IStopAssistingCustomerResult> => {
        signals.csr.onBeforeStopAssistingCustomer.emit();
        await ajax
            .post(`${userURL}stop_assisting/`)
            .set("Accept", "application/json")
            .set(CSRF_HEADER, await getCSRFToken());
        const [customerResp, basketResp] = await Promise.all([
            ajax.get("/api/csr/users/currently_assisted_user/"),
            ajax.get("/api/csr/baskets/currently_assisted_basket/"),
        ]);
        signals.csr.onAfterStopAssistingCustomer.emit();
        return {
            customer: customerResp.body as ICustomerSummary,
            basket: basketResp.body as IBasket,
        };
    },
);
